import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

import { NerisApi } from '@/apis';
import { ConfirmTypes } from '@/components';
import { useAppDispatch } from '@/hooks';
import { EntityResponse, EntityStation, NerisEnrollmentStatus } from '@/models';
import { showToast } from '@/store';

import { AccountContext } from './AccountContext';

interface NerisContextType {
  nerisEntityId: string;
  setNerisEntityId: (nerisEntityId: string) => void;
  isCheckingEnrollment: boolean;
  checkEnrollment: (nerisEntityId: string) => Promise<NerisEnrollmentStatus>;
  enrollmentStatus: NerisEnrollmentStatus;
  stations: EntityStation[];
  entity: EntityResponse | null;
  getEntity: (nerisEntityId: string) => Promise<void>;
  setNerisModuleEnabled: (nerisModuleEnabled: boolean) => void;
  isNerisModuleEnabled: boolean;
}

export const NerisContext = createContext<NerisContextType>({
  nerisEntityId: '',
  setNerisEntityId: () => {},
  isCheckingEnrollment: false,
  checkEnrollment: async () => NerisEnrollmentStatus.NOT_ENROLLED,
  enrollmentStatus: NerisEnrollmentStatus.NONE,
  stations: [],
  entity: null,
  getEntity: async () => {},
  setNerisModuleEnabled: () => {},
  isNerisModuleEnabled: false,
});

interface NerisContextProviderProps {
  children: ReactNode;
}

export const NerisContextProvider: FC<NerisContextProviderProps> = (props) => {
  const { children } = props;
  const { account } = useContext(AccountContext);
  const dispatch = useAppDispatch();
  const [nerisEntityId, setNerisEntityId] = useState<string>(
    account?.settings?.features?.neris?.entityId || '',
  );
  const [isCheckingEnrollment, setIsCheckingEnrollment] =
    useState<boolean>(false);
  const [enrollmentStatus, setEnrollmentStatus] =
    useState<NerisEnrollmentStatus>(NerisEnrollmentStatus.NONE);
  const [stations, setStations] = useState<EntityStation[]>([]);
  const [entity, setEntity] = useState<EntityResponse | null>(null);
  const [isNerisModuleEnabled, setIsNerisModuleEnabled] =
    useState<boolean>(false);

  // evaluate enrollment status on mount
  useEffect(() => {
    if (account?.settings?.features?.neris?.enabled) {
      setIsNerisModuleEnabled(true);
      if (account?.settings?.features?.neris?.entityId) {
        setEnrollmentStatus(NerisEnrollmentStatus.ENROLLED);
      } else {
        setEnrollmentStatus(NerisEnrollmentStatus.NOT_ENROLLED);
      }
    }
  }, [account?.settings?.features?.neris?.enabled]);

  const checkEnrollment = async (
    nerisEntityId: string,
  ): Promise<NerisEnrollmentStatus> => {
    setIsCheckingEnrollment(true);
    setEnrollmentStatus(NerisEnrollmentStatus.CHECKING);
    try {
      const response = await NerisApi.checkEnrollment(nerisEntityId);
      const status = response.data.status;
      const entityData = response.data.entity;

      setEnrollmentStatus(status);

      if (entityData) {
        setEntity(entityData);
        setStations(entityData.stations || []);
      }

      return status;
    } catch (error) {
      console.error('error checking enrollment', error);
      setEnrollmentStatus(NerisEnrollmentStatus.NOT_ENROLLED);
      return NerisEnrollmentStatus.NOT_ENROLLED;
    } finally {
      setIsCheckingEnrollment(false);
    }
  };

  const setNerisModuleEnabled = (isNerisModuleEnabled: boolean) => {
    setIsNerisModuleEnabled(isNerisModuleEnabled);
  };

  const getEntity = async (nerisEntityId: string) => {
    try {
      const response = await NerisApi.getEntity(nerisEntityId);
      const entityData = response.data;
      setEntity(entityData);
      setStations(entityData.stations || []);
    } catch (error) {
      console.error('Failed to fetch NERIS entity:', error);
      dispatch(
        showToast({
          message:
            'Failed to fetch NERIS entity, please refresh the page and try again.',
          type: ConfirmTypes.ERROR,
        }),
      );
      throw error;
    }
  };

  return (
    <NerisContext.Provider
      value={{
        nerisEntityId,
        setNerisEntityId,
        isCheckingEnrollment,
        checkEnrollment,
        enrollmentStatus,
        stations,
        entity,
        getEntity,
        setNerisModuleEnabled,
        isNerisModuleEnabled,
      }}
    >
      {children}
    </NerisContext.Provider>
  );
};
